import injector from '@app/services';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { Checkbox, FormControl, ListItemText, MenuItem, TextField } from '@material-ui/core';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

const intlService: IntlService = injector.get(IntlService);

const MultiSelectField: React.FC<IMultiSelectFieldProps> = ({
	disabled,
	id,
	menuItems,
	onSave,
	saveButtonText,
	label,
}) => {
	const [isAllSelected, setIsAllSelected] = useState(true);
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState<string[]>(
		menuItems.map((x) => x.value).concat([intlService.GetFormattedMessage('app.filter-all')])
	);
	const prevValue = useRef(menuItems.filter((x) => x.selected).map((x) => x.value));
	const [items, setItems] = useState<IMultiSelectMenuItem[]>(menuItems.slice());
	const [isSaved, setIsSaved] = useState(false);
	const [isOnlyDefaultItemsAreSelected, setIsOnlyDefaultItemsAreSelected] = useState(
		items.filter((x) => x.selected).every((x) => x.isDefault)
	);

	useEffect(() => {
		prevValue.current = SharedService.filterAndMap(
			menuItems,
			(x) => x.selected,
			(x) => x.value
		);
	}, [menuItems]);

	useEffect(() => {
		const isSelectAllUnselected = isAllSelected && items.some((x) => !x.selected);
		if (isSelectAllUnselected) {
			const selectedItemsValues = items.map((x) => x.value);
			setValue(selectedItemsValues);
			handleOnItemChange(selectedItemsValues);
		} else {
			const selectedItems = items.filter((x) => x.selected);
			setValue(selectedItems.map((x) => x.value));
			setIsOnlyDefaultItemsAreSelected(selectedItems.every((x) => x.isDefault));
		}
	}, [items]);

	const handleOnItemChange = (selectedValues: string[]) => {
		const updatedMenuItems = items.slice();

		items.forEach((item, i) => {
			updatedMenuItems[i] = {
				...updatedMenuItems[i],
				selected: selectedValues.includes(item.value),
			};
		});

		setItems(updatedMenuItems);
	};

	const handleOnChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const values = event.target.value as string[];
		const valuesWithoutDoneAndSave = values.filter(
			(x) => ![intlService.GetFormattedMessage('app.filter-all'), saveButtonText].includes(x)
		);

		if (values.includes(saveButtonText)) {
			// If done is selected
			onSave(valuesWithoutDoneAndSave);
			setIsSaved(true);
			setOpen(false);
		} else if (isAllSelected && valuesWithoutDoneAndSave.length !== menuItems.length) {
			// If Selected while select all is selected
			setIsAllSelected(false);
			handleOnItemChange(valuesWithoutDoneAndSave);
		} else if (
			(isAllSelected && values.includes(intlService.GetFormattedMessage('app.filter-all'))) ||
			valuesWithoutDoneAndSave.length === 0
		) {
			// If Select All is UnSelected OR no value is selected
			setIsAllSelected(false);
			handleOnItemChange(menuItems.filter((x) => x.isDefault).map((x) => x.value));
		} else if (values.includes(intlService.GetFormattedMessage('app.filter-all'))) {
			// If Select All is Selected
			setIsAllSelected(true);
			handleOnItemChange(menuItems.map((x) => x.value));
		} else {
			// if selected while select all is not selected
			handleOnItemChange(valuesWithoutDoneAndSave);
			setIsAllSelected(valuesWithoutDoneAndSave.length === menuItems.length);
		}
	};

	const handleOnOpen = () => {
		setOpen(true);
		setIsSaved(false);
	};

	const handleOnClose = () => {
		setOpen(false);

		if (!isSaved) {
			handleOnItemChange(prevValue.current);
			setIsAllSelected(prevValue.current.length === menuItems.length);
		}
	};

	const handleRenderValue = (selectedValues: string[]) => {
		const values: string[] = [];

		items.map((e) => {
			if (selectedValues.includes(e.value)) {
				values.push(e.text);
			}
			return e;
		});
		return isAllSelected ? intlService.GetFormattedMessage('app.filter-all') : values.join(', ');
	};

	return (
		<FormControl fullWidth={true}>
			<TextField
				label={label}
				id={id}
				select={true}
				value={value}
				onChange={handleOnChange}
				SelectProps={{
					open,
					multiple: true,
					onClose: handleOnClose,
					onOpen: handleOnOpen,
					renderValue: handleRenderValue,
				}}
				disabled={disabled}
				variant='outlined'
			>
				<MenuItem
					key={intlService.GetFormattedMessage('app.filter-all')}
					value={intlService.GetFormattedMessage('app.filter-all')}
				>
					<Checkbox checked={isAllSelected} />
					<ListItemText primary={intlService.GetFormattedMessage('app.filter-select-all')} />
				</MenuItem>
				{items.map((item) => (
					<MenuItem
						key={item.value}
						value={item.value}
						disabled={item.disabled || (isOnlyDefaultItemsAreSelected && item.isDefault)}
					>
						<Checkbox
							checked={item.selected}
							disabled={item.disabled || (isOnlyDefaultItemsAreSelected && item.isDefault)}
						/>
						<ListItemText primary={item.text} />
					</MenuItem>
				))}
				<MenuItem className='text-center' key={saveButtonText} value={saveButtonText}>
					<ListItemText primary={saveButtonText} />
				</MenuItem>
			</TextField>
		</FormControl>
	);
};

interface IMultiSelectFieldProps {
	name: string;
	id: string;
	label: string;
	placeholder?: string;
	disabled?: boolean;
	saveButtonText: string;
	onSave: (selectedItems: string[]) => void;
	menuItems: IMultiSelectMenuItem[];
}

export interface IMultiSelectMenuItem {
	text: string;
	value: string;
	selected?: boolean;
	isDefault?: boolean;
	disabled?: boolean;
}

export default MultiSelectField;



// WEBPACK FOOTER //
// ./src/components/shared/multi-select-field.component.tsx